/**
 * Personalakte-Tab „App-Freigaben": 4 Switches (leitstand/dokubase/
 * besuchermanagement/ninox). Identity-Regel G-28: Freigabe erfordert
 * gesetzten Benutzernamen (422 → Fehler-Toast). Benutzername-Feld daneben —
 * editierbar nur fuer admin.
 */

import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { useCan } from "@/lib/permissions"
import { api, apiError } from "@/services/api"
import type { EmployeeDetail, EmployeeUpdate } from "@/types"

type ApprovalField =
  | "leitstandApproved"
  | "dokubaseApproved"
  | "besuchermanagementApproved"
  | "ninoxApproved"

const APPROVALS: { field: ApprovalField; label: string; description: string }[] = [
  { field: "leitstandApproved", label: "Leitstand", description: "Produktionssteuerung" },
  { field: "dokubaseApproved", label: "Dokubase", description: "Dokumentation (BookStack)" },
  {
    field: "besuchermanagementApproved",
    label: "Besuchermanagement",
    description: "Besucher-App",
  },
  { field: "ninoxApproved", label: "Ninox", description: "Ninox-Datenbank" },
]

export function ApprovalsTab({
  detail,
  canWrite,
}: {
  detail: EmployeeDetail
  canWrite: boolean
}) {
  const queryClient = useQueryClient()
  const isAdmin = useCan("admin")
  const [username, setUsername] = useState(detail.username ?? "")

  const update = useMutation({
    mutationFn: (body: EmployeeUpdate) => api.employees.update(detail.id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee", detail.id] })
      queryClient.invalidateQueries({ queryKey: ["employees"] })
      toast.success("Gespeichert")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardContent className="space-y-4 py-4">
          <h3 className="text-base font-semibold">App-Freigaben</h3>
          {APPROVALS.map(({ field, label, description }) => (
            <div key={field} className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor={field}>{label}</Label>
                <p className="text-xs text-muted-foreground">{description}</p>
              </div>
              <Switch
                id={field}
                checked={detail[field]}
                disabled={!canWrite || update.isPending}
                onCheckedChange={(checked) => update.mutate({ [field]: checked })}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 py-4">
          <h3 className="text-base font-semibold">Benutzername</h3>
          <p className="text-sm text-muted-foreground">
            App-Freigaben erfordern einen gesetzten Benutzernamen (G-28). Er wird beim Anlegen
            automatisch generiert und ändert sich bei Umbenennung nicht automatisch.
          </p>
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="approval-username">Benutzername</Label>
              <Input
                id="approval-username"
                value={username}
                readOnly={!isAdmin}
                disabled={!isAdmin}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            {isAdmin && (
              <Button
                disabled={update.isPending || username.trim() === (detail.username ?? "")}
                onClick={() => update.mutate({ username: username.trim() || null })}
              >
                Speichern
              </Button>
            )}
          </div>
          {!isAdmin && (
            <p className="text-xs text-muted-foreground">
              Nur Administratoren dürfen den Benutzernamen ändern.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
